import React from 'react';
import { FileSizeOptions, filesize } from 'filesize';
import bytes from 'bytes';
import { observer } from 'mobx-react';
import { Progress } from '@wikifactory/voxel-ui';
import { WithNamespaces, withNamespaces } from 'react-i18next';
import { SectionTitle } from 'apps/group/styles';

import { FullWidthDivider, SettingsSection } from 'legacyComponents/styled/lib';
import { themes } from 'styles/themes/wikifactoryNew';
import { Spinner } from 'components';
import { StorageUsageTotalMap, SubscriptionType } from 'constants/subscription';

type StorageUsageProps = {
  storageUsage: {
    usedSize: number;
  };
  loading: boolean;
  subscriptionType: SubscriptionType;
} & WithNamespaces;

const style: React.CSSProperties = {
  height: '1.125rem',
  borderRadius: '0.5rem',
  backgroundColor: `${themes.color.wfGrey950}`,
};

export const StorageUsage = withNamespaces()(
  observer(
    ({ t, loading, storageUsage, subscriptionType }: StorageUsageProps) => {
      if (!subscriptionType) {
        return null;
      }

      if (loading) {
        return (
          <div className=' mt-8 w-full'>
            <SettingsSection>
              <SectionTitle level={2} size='medium'>
                {t('Storage Usage')}
              </SectionTitle>
              <FullWidthDivider />
              <Spinner margin='20px auto' />
            </SettingsSection>
          </div>
        );
      }

      const { usedSize = 0 } = storageUsage || {};

      const total = StorageUsageTotalMap[subscriptionType];
      const usedPercentage = usedSize / bytes(total);
      const isStorageFull = usedPercentage >= 1;

      if (!total) return null;

      const fileSizeOptions: FileSizeOptions = { standard: 'jedec' };
      const usedText = filesize(usedSize, fileSizeOptions);
      const remainingText = filesize(
        isStorageFull ? 0 : bytes(total) - usedSize,
        fileSizeOptions,
      );

      const getUsageStatusColor = () => {
        if (usedPercentage >= 0.8) {
          return themes.color.wfOrange;
        }
        if (usedPercentage >= 0.6) {
          return themes.color.wfYellow;
        }
        return themes.color.wfBlue;
      };

      return (
        <div className='mt-8 w-full text-black font-hel'>
          <SectionTitle style={{ marginBottom: 0 }} level={2} size='medium'>
            {t('Storage Usage')}
          </SectionTitle>

          <FullWidthDivider />
          <div className='flex mb-2 mt-5 justify-between items-center text-black font-hel'>
            <div className='font-bold'>{`${usedText} of ${total} used`}</div>
            <div>{`${remainingText} remaining`}</div>
          </div>
          <Progress
            className='font-hel'
            variant='determinate'
            type='linear'
            value={Math.ceil(isStorageFull ? 100 : usedPercentage * 100)}
            style={{
              ...style,
            }}
            sx={{
              '.MuiLinearProgress-root': {
                ...style,
                backgroundColor: `${themes.color.wfGrey950} !important`,
              },
              '.MuiLinearProgress-bar': {
                ...style,
                background: getUsageStatusColor(),
              },
            }}
          />
        </div>
      );
    },
  ),
);
